import dayjs from 'dayjs';
import { hasValue } from './ArrayHelper';
import { nearestCent } from './CurrencyHelper';

export function titleCase(s: string) {
    s = `${s[0]?.toUpperCase() ?? ''}${s.substring(1)}`;
    s = s.replace(/(\s\w)/g, (r) => r.toUpperCase());
    return s;
}

export function capitalize(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export function asNumber(s: string | null | undefined): number | undefined {
    return asNumberOrDefault(s, undefined);
}

export function asNumberOrDefault<D>(s: string | null | undefined, defaultValue: D): number | D {
    if (!hasValue(s)) return defaultValue;
    if (s === '') return defaultValue;
    const n = Number(s);
    if (Number.isNaN(n)) return defaultValue;
    return n;
}

export function asNumberOrThrow(s: string | null | undefined): number {
    const res = asNumberOrDefault(s, undefined);
    if (!hasValue(res)) throw new Error(`Couldn't convert ${s ?? 'undefined'} to number`);
    return res;
}

export function formatDate(s: string | dayjs.Dayjs) {
    return dayjs(s).format('DD MMMM YYYY');
}

export function formatTimestamp(s: string | dayjs.Dayjs) {
    return dayjs(s).format('ddd, D MMM YYYY h:mma');
}

/**
 * As formatTimestamp, with the viewer's timezone appended. Use where the reader could
 * reasonably take the time to be in some other zone: backup times in particular, since
 * the backup *schedule* is Sydney-based for every server region, so a customer outside
 * Sydney is comparing two different zones (#1923).
 *
 * The zone is resolved against the supplied timestamp, not "now", so it reports the
 * offset actually in force then (AEST vs AEDT).
 */
export function formatTimestampWithZone(s: string | Date | dayjs.Dayjs) {
    const timestamp = dayjs(s);
    // formatToParts throws RangeError on a non-finite date where dayjs renders
    // "Invalid Date", and a throw from render would take out the whole page.
    if (!timestamp.isValid()) return formatTimestamp(timestamp);

    // Locale is pinned rather than left to the browser to keep this an abbreviation
    // ("AEST") instead of a locale-dependent numeric offset ("GMT+10"); the dayjs
    // formats above are likewise fixed to English.
    const zone = new Intl.DateTimeFormat('en-AU', { timeZoneName: 'short' })
        .formatToParts(timestamp.toDate())
        .find((p) => p.type === 'timeZoneName')?.value;

    return zone ? `${formatTimestamp(timestamp)} ${zone}` : formatTimestamp(timestamp);
}

export function formatCurrency(value: number) {
    return '$' + nearestCent(value).toFixed(2);
}

export function padStart(s: string, length: number, padChar: string) {
    let output = s;
    while (output.length < length) {
        output = padChar + output;
    }
    return output;
}
